import { Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { PageMeta } from '@/components/seo/PageMeta'
import { LandingLayout } from '@/components/layout/LandingLayout'
import { HeroReveal, Pressable } from '@/components/motion'

const wayfinding = [
  { label: 'Pricing', href: '/pricing' },
  { label: 'Customer stories', href: '/customers' },
  { label: 'About', href: '/about' },
  { label: 'Contact', href: '/contact' },
]

/**
 * 404: the number is the moment. Three giant display digits, the middle zero
 * knocked out of a rotated blue highlight box, then the message, two clear
 * ways back and a hairline row of the main destinations.
 */
export default function NotFound() {
  return (
    <>
      <PageMeta title="Page Not Found" description="The page you're looking for doesn't exist." noIndex />

      <LandingLayout>
        <section className="landing-container flex min-h-[calc(100svh-72px)] flex-col pt-10 pb-12 md:pt-16">
          <HeroReveal delay={0.05} className="flex flex-1 items-center">
            <p
              aria-hidden
              className="w-full select-none font-[family-name:var(--font-landing-display)] text-[clamp(6.5rem,min(27vw,42svh),26rem)] leading-[1.3] tracking-[-0.04em] text-[var(--color-landing-gray-900)] dark:text-white"
            >
              4
              <span className="mx-[0.04em] inline-block -rotate-2 bg-[var(--color-landing-accent)] px-[0.08em] leading-[1.3] text-white">0</span>
              4
            </p>
          </HeroReveal>

          <div className="mt-8 grid gap-10 border-t border-[var(--color-landing-gray-900)] pt-10 dark:border-white/40 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-end lg:gap-24">
            <HeroReveal delay={0.2}>
              <h1 className="landing-text-display-xl text-[var(--color-landing-gray-900)] dark:text-white"><span className="sr-only">Error 404: </span>Page not found.</h1>
              <p className="mt-6 max-w-[44ch] border-l border-[var(--color-landing-gray-300)] pl-5 landing-text-body-lg text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                Sorry, we couldn't find the page you're looking for.
              </p>
            </HeroReveal>

            <HeroReveal delay={0.3} className="flex flex-col gap-8 lg:items-end">
              <div className="flex flex-wrap gap-4">
                <Pressable>
                  <Link to="/" className="landing-cta landing-cta--solid landing-cta--lg landing-btn-sweep">
                    Go home
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Pressable>
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="landing-cta landing-cta--outline landing-cta--lg landing-btn-sweep"
                >
                  <ArrowLeft className="h-5 w-5" />
                  Go back
                </button>
              </div>
              <nav aria-label="Popular pages">
                <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold uppercase tracking-wide lg:justify-end">
                  {wayfinding.map((item) => (
                    <li key={item.href}>
                      <Link
                        to={item.href}
                        className="text-[var(--color-landing-gray-700)] underline decoration-[var(--color-landing-gray-300)] underline-offset-[6px] transition-colors hover:text-[var(--color-landing-accent)] hover:decoration-[var(--color-landing-accent)] dark:text-[var(--color-landing-gray-300)] dark:hover:text-white"
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            </HeroReveal>
          </div>
        </section>
      </LandingLayout>
    </>
  )
}
